import React from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, ResponsiveContainer, Tooltip
} from 'recharts';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';

// ── motion presets ──────────────────────────────────────────────
const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const COVER_IMG =
  'https://images.unsplash.com/photo-1550345332-09e3ac987658?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

// Wrapper that turns off motion in print mode (renders plain div)
const Section = ({ children, className = '', testid, printMode }) => {
  if (printMode) {
    return <section data-testid={testid} className={className}>{children}</section>;
  }
  return (
    <motion.section {...reveal} data-testid={testid} className={className}>
      {children}
    </motion.section>
  );
};

const SECTION = 'relative px-6 sm:px-12 py-20 sm:py-28';
const CONTAINER = 'max-w-6xl mx-auto w-full';

// ── 1. Cover ────────────────────────────────────────────────────
const Cover = ({ s, quote, printMode }) => (
  <section
    data-testid="eoae-cover"
    className="relative min-h-screen flex flex-col justify-end px-6 sm:px-12 py-16 overflow-hidden eoae-avoid-break"
  >
    <div className="absolute inset-0">
      <img src={COVER_IMG} alt="" className="w-full h-full object-cover opacity-40 grayscale" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/40" />
    </div>
    <div className="relative z-10 max-w-6xl mx-auto w-full">
      <p className="font-oswald uppercase tracking-[0.4em] text-[#D4AF37] text-sm sm:text-base mb-6">
        End of an Era
      </p>
      <h1 className="font-oswald uppercase tracking-tight text-white text-5xl sm:text-8xl leading-[0.95] mb-8">
        {s.client.name}
      </h1>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#A1A1AA] font-manrope">
        <span className="text-base sm:text-lg">{fmtDate(s.period.start)} — {fmtDate(s.period.end)}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] hidden sm:block" />
        <span className="text-base sm:text-lg">{s.period.time_together} de viaje</span>
      </div>
      {quote && (
        <p className="font-cormorant italic text-white/80 text-2xl sm:text-3xl mt-10 max-w-2xl leading-relaxed">
          “{quote}”
        </p>
      )}
      <p className="font-manrope text-[#52525B] text-sm mt-12">{s.center}</p>
    </div>
  </section>
);

// ── 2. The Journey ──────────────────────────────────────────────
const Stat = ({ value, label, suffix }) => (
  <div className="eoae-avoid-break">
    <div className="font-anton text-white text-6xl sm:text-8xl leading-none tracking-tight drop-shadow-[0_0_20px_rgba(255,255,255,0.15)]">
      {value}<span className="text-[#D4AF37] text-4xl sm:text-5xl align-top">{suffix}</span>
    </div>
    <div className="font-oswald uppercase tracking-widest text-[#A1A1AA] text-xs sm:text-sm mt-3">{label}</div>
  </div>
);

const Journey = ({ s, printMode }) => {
  const j = s.journey;
  return (
    <Section className={SECTION} testid="eoae-journey" printMode={printMode}>
      <div className={CONTAINER}>
        <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] text-3xl sm:text-5xl mb-16">
          El viaje
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          <Stat value={j.total_sessions} label="Sesiones completadas" />
          <Stat value={j.active_weeks} label="Semanas activas" />
          <Stat value={j.hours_trained} label="Horas entrenadas" suffix="h" />
          <Stat value={j.adherence_percent} label="Adherencia" suffix="%" />
          <Stat value={j.cycles_completed} label="Ciclos completados" />
          <Stat value={s.achievements.pr_count} label="Récords personales" />
        </div>
      </div>
    </Section>
  );
};

// ── 3 + 4. Hall of Fame (every PR) grouped & colour-coded by category ──
// Category colours harmonised into the dark gold "Trophy Room" aesthetic,
// consistent with the app-wide category palette (chest=red, back=blue, …).
const CAT_STYLES = {
  chest:        { label: 'Pecho',           bar: 'bg-red-600',     glow: 'shadow-[0_0_12px_rgba(220,38,38,0.45)]',  border: 'border-t-red-600',     grad: 'from-red-900/25' },
  back:         { label: 'Espalda',         bar: 'bg-blue-600',    glow: 'shadow-[0_0_12px_rgba(37,99,235,0.45)]',  border: 'border-t-blue-600',    grad: 'from-blue-900/25' },
  legs:         { label: 'Pierna',          bar: 'bg-emerald-600', glow: 'shadow-[0_0_12px_rgba(5,150,105,0.45)]',  border: 'border-t-emerald-600', grad: 'from-emerald-900/25' },
  shoulders:    { label: 'Hombro',          bar: 'bg-orange-600',  glow: 'shadow-[0_0_12px_rgba(234,88,12,0.45)]',  border: 'border-t-orange-600',  grad: 'from-orange-900/25' },
  arms:         { label: 'Brazo',           bar: 'bg-purple-600',  glow: 'shadow-[0_0_12px_rgba(124,58,237,0.45)]', border: 'border-t-purple-600',  grad: 'from-purple-900/25' },
  core:         { label: 'Core',            bar: 'bg-yellow-500',  glow: 'shadow-[0_0_12px_rgba(202,138,4,0.45)]',  border: 'border-t-yellow-500',  grad: 'from-yellow-900/25' },
  'full-body':  { label: 'Cuerpo completo', bar: 'bg-teal-500',    glow: 'shadow-[0_0_12px_rgba(13,148,136,0.45)]', border: 'border-t-teal-500',    grad: 'from-teal-900/25' },
  other:        { label: 'Otros',           bar: 'bg-zinc-500',    glow: '',                                        border: 'border-t-zinc-500',    grad: 'from-zinc-800/30' },
};
const catStyle = (c) => CAT_STYLES[c] || CAT_STYLES.other;

const CategoryBanner = ({ cat, catKey, count }) => (
  <div
    className="flex items-center gap-4 pb-2 mb-1 w-full border-b border-[#D4AF37]/10 print:border-b-[#333]"
    data-testid={`category-banner-${catKey}`}
  >
    <div className={`w-1.5 h-7 rounded-full ${cat.bar} ${cat.glow} print:shadow-none`} />
    <h3 className="font-oswald uppercase text-xl sm:text-2xl tracking-[0.2em] text-[#D4AF37]">{cat.label}</h3>
    <span className="font-manrope text-xs sm:text-sm font-medium text-[#D4AF37]/50 ml-auto tracking-wide uppercase">
      {count} {count === 1 ? 'récord' : 'récords'}
    </span>
  </div>
);

const HofCard = ({ pr, cat }) => {
  const media = getExerciseAnimationUrl(pr);
  return (
    <div
      data-testid="hof-card"
      className={`eoae-avoid-break relative rounded-2xl bg-[#0a0a0a] ring-1 ring-white/10 border-t-2 ${cat.border} overflow-hidden group transition-shadow duration-500 hover:shadow-[0_0_28px_rgba(212,175,55,0.12)]`}
    >
      {/* category-tinted top wash */}
      <div className={`absolute top-0 inset-x-0 h-32 bg-gradient-to-b ${cat.grad} to-transparent opacity-50 group-hover:opacity-80 transition-opacity duration-500 pointer-events-none`} />
      {/* Exercise animation/GIF — prominent collectible-card media */}
      <div className="relative h-44 overflow-hidden">
        {media ? (
          <img
            src={media}
            alt={pr.exercise_name}
            loading="lazy"
            className="w-full h-full object-contain p-2"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#D4AF37]/40 text-5xl">🏆</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-3 right-3 font-anton text-white text-2xl tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
          {pr.weight}<span className="text-[#D4AF37] text-base align-top">kg</span>
        </div>
      </div>
      {/* Details */}
      <div className="relative px-5 pb-5 pt-3">
        <h3 className="font-oswald uppercase tracking-tight text-white text-base leading-tight min-h-[2.4rem]">
          {pr.exercise_name}
        </h3>
        <p className="font-manrope text-[#A1A1AA] text-sm mt-1">{pr.reps} reps · {fmtDate(pr.date)}</p>
        {pr.improvement_pct ? (
          <p className="font-manrope text-green-400 text-sm mt-2 font-semibold">
            +{pr.improvement_kg} kg · +{pr.improvement_pct}%
          </p>
        ) : (
          <p className="font-manrope text-[#52525B] text-sm mt-2">Récord absoluto</p>
        )}
      </div>
    </div>
  );
};

const HallOfFame = ({ s, printMode }) => {
  const hof = s.hall_of_fame || [];
  if (hof.length === 0) return null;

  // Group by category, preserving the (already category-ordered) backend order
  const groups = [];
  const idx = {};
  hof.forEach((pr) => {
    const c = pr.category || 'other';
    if (!(c in idx)) { idx[c] = groups.length; groups.push([c, []]); }
    groups[idx[c]][1].push(pr);
  });

  return (
    <Section className={SECTION} testid="eoae-hall-of-fame" printMode={printMode}>
      <div className={CONTAINER}>
        <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] text-3xl sm:text-5xl mb-4">
          Salón de la fama
        </h2>
        <p className="font-manrope text-[#A1A1AA] mb-12 max-w-xl">
          Tus mejores marcas, agrupadas por zona del cuerpo. Cada una cuenta una historia de esfuerzo.
        </p>
        <div className="flex flex-col gap-12 w-full">
          {groups.map(([key, cards]) => {
            const cat = catStyle(key);
            return (
              <div key={key} className="flex flex-col gap-5 eoae-avoid-break">
                <CategoryBanner cat={cat} catKey={key} count={cards.length} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cards.map((pr) => <HofCard key={pr.exercise_id} pr={pr} cat={cat} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

// ── 5. Achievements Unlocked ────────────────────────────────────
const Badge = ({ m }) => (
  <div
    data-testid="achievement-badge"
    className={`eoae-avoid-break flex flex-col items-center text-center gap-3 rounded-2xl p-6 transition-all duration-500 ${
      m.unlocked
        ? 'border border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/20 to-transparent shadow-[0_0_25px_rgba(212,175,55,0.18)]'
        : 'opacity-40 grayscale border border-white/10 bg-white/5'
    }`}
  >
    <span className="text-4xl sm:text-5xl">{m.icon}</span>
    <span className="font-oswald uppercase tracking-wide text-white text-xs sm:text-sm leading-tight">{m.label}</span>
    <span className={`font-manrope text-[10px] uppercase tracking-widest ${m.unlocked ? 'text-[#D4AF37]' : 'text-[#52525B]'}`}>
      {m.unlocked ? 'Desbloqueado' : 'Bloqueado'}
    </span>
  </div>
);

const Achievements = ({ s, printMode }) => (
  <Section className={SECTION} testid="eoae-achievements" printMode={printMode}>
    <div className={CONTAINER}>
      <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] text-3xl sm:text-5xl mb-12">
        Logros desbloqueados
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
        {(s.milestones || []).map((m) => <Badge key={m.key} m={m} />)}
      </div>
    </div>
  </Section>
);

// ── 6. Progress chart (editorial) ───────────────────────────────
const Progress = ({ s, printMode }) => {
  const data = s.progress?.sessions_per_month || [];
  if (data.length < 2) return null;
  return (
    <Section className={SECTION} testid="eoae-progress" printMode={printMode}>
      <div className={CONTAINER}>
        <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] text-3xl sm:text-5xl mb-12">
          Tu constancia
        </h2>
        <div className="h-72 w-full eoae-avoid-break">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 12, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ stroke: 'rgba(212,175,55,0.3)' }}
                contentStyle={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '9999px', padding: '6px 16px', color: '#fff' }}
                labelStyle={{ color: '#D4AF37' }}
                formatter={(v) => [`${v} sesiones`, '']}
              />
              <Area type="monotone" dataKey="sessions" stroke="#D4AF37" strokeWidth={2} fill="url(#goldGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Section>
  );
};

// ── 7. Legacy (trainer message + AI reflection) ─────────────────
const Legacy = ({ s, trainerMessage, reflection, printMode }) => {
  if (!trainerMessage && !reflection) return null;
  return (
    <Section className={`${SECTION} bg-[#0a0a0a]`} testid="eoae-legacy" printMode={printMode}>
      <div className="max-w-3xl mx-auto">
        <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] text-3xl sm:text-5xl mb-12 text-center">
          Tu legado
        </h2>
        {reflection && (
          <div className="font-cormorant text-white/90 text-xl sm:text-2xl leading-loose space-y-6">
            {reflection.split('\n').filter(Boolean).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}
        {trainerMessage && (
          <div className="mt-14 pt-10 border-t border-white/10 eoae-avoid-break">
            <p className="font-cormorant italic text-white/85 text-xl sm:text-2xl leading-relaxed">
              “{trainerMessage}”
            </p>
            <p className="font-oswald uppercase tracking-widest text-[#D4AF37] text-sm mt-6 text-right">
              — {s.trainer.name}
            </p>
          </div>
        )}
      </div>
    </Section>
  );
};

// ── 8. ¿Qué viene ahora? (closing credits) ──────────────────────
const Credit = ({ role, name }) => (
  <div className="flex items-baseline justify-between gap-8 max-w-md mx-auto w-full">
    <span className="font-oswald uppercase tracking-widest text-[#52525B] text-sm">{role}</span>
    <span className="font-manrope text-white text-lg text-right">{name}</span>
  </div>
);

const Closing = ({ s, printMode }) => (
  <section
    data-testid="eoae-closing"
    className="relative px-6 sm:px-12 py-28 min-h-screen flex flex-col justify-center bg-[#050505] eoae-avoid-break"
  >
    <div className="max-w-3xl mx-auto w-full text-center">
      <h2 className="font-oswald uppercase tracking-tight text-white text-3xl sm:text-5xl mb-12">
        ¿Qué viene ahora?
      </h2>
      <div className="font-cormorant text-white/80 text-xl sm:text-2xl leading-loose space-y-5 max-w-2xl mx-auto">
        <p>No sabemos qué caminos tomará la vida a partir de aquí.</p>
        <p>Quizá vuelvas a entrenar con nosotros.</p>
        <p>Quizá continúes tu camino por tu cuenta.</p>
        <p>Quizá descubras nuevas etapas.</p>
        <p className="text-[#D4AF37]">Pero todo lo que construiste aquí ya forma parte de ti.</p>
      </div>
      <div className="mt-16 space-y-4">
        <Credit role="Cliente" name={s.client.name} />
        <Credit role="Entrenador" name={s.trainer.name} />
        <Credit role="Centro" name={s.center} />
        <Credit role="Periodo" name={`${fmtDate(s.period.start)} — ${fmtDate(s.period.end)}`} />
      </div>
      <p className="font-manrope text-[#52525B] text-xs uppercase tracking-[0.3em] mt-16">
        End of an Era
      </p>
    </div>
  </section>
);

// ── Root experience ─────────────────────────────────────────────
const EoaEExperience = ({ report, printMode = false }) => {
  if (!report || !report.snapshot) return null;
  const s = report.snapshot;
  return (
    <main className="eoae-root bg-[#050505] text-white min-h-screen font-manrope" data-testid="eoae-experience">
      <Cover s={s} quote={report.optional_quote} printMode={printMode} />
      <Journey s={s} printMode={printMode} />
      <HallOfFame s={s} printMode={printMode} />
      <Achievements s={s} printMode={printMode} />
      <Progress s={s} printMode={printMode} />
      <Legacy
        s={s}
        trainerMessage={report.trainer_message}
        reflection={report.ai_reflection}
        printMode={printMode}
      />
      <Closing s={s} printMode={printMode} />
    </main>
  );
};

export default EoaEExperience;
